import { notNull } from '@eturi/util'
import type { Onboarding } from '@op/services'
import { filterOnboardingByAge, getOnboardingLabel, type OnboardingUser } from '@op/services'
import sortBy from 'lodash/sortBy'
import { useSelector } from 'react-redux'
import { currentLang$ } from '../../../compound-selectors/language'
import { useAllowanceOpts } from '../../Allowance/useAllowanceOpts'
import {
	APP_RULE_STATUS_ICON_MAP,
	APP_RULE_STATUS_TEXT_MAP,
	getRuleStatusColorMap,
	LOCATION_ALERT_ICON_MAP,
	SCHEDULE_ICON_MAP,
	VEW_STATUS_TEXT_MAP,
} from '../constants'
import type { ColRuleItemWithId, LabeledIconWithId, SectionBoxSmallWithId } from '../types'
import { getOnboardingAllowance } from '../utils/getOnboardingAllowance'

export const useOnboardingSummary = (onboarding: Onboarding, selectedUser: OnboardingUser) => {
	const lang = useSelector(currentLang$)
	const allowanceOpts = useAllowanceOpts()
	const userAge = selectedUser.age

	const appRules = sortBy(
		filterOnboardingByAge(onboarding.app_rules, userAge).map(
			(rule): ColRuleItemWithId => ({
				id: rule.id,
				colorClassName: getRuleStatusColorMap[rule.directive],
				icon: APP_RULE_STATUS_ICON_MAP[rule.directive],
				subtitle: APP_RULE_STATUS_TEXT_MAP[rule.directive],
				title: getOnboardingLabel(rule.label, lang),
			}),
		),
		'order',
	)
	const locationAlerts = sortBy(
		filterOnboardingByAge(onboarding.geofences, userAge)
			.map((geofence): LabeledIconWithId | null => {
				const icon = LOCATION_ALERT_ICON_MAP.get(geofence.content_key)
				return icon ?
						{ id: geofence.content_key, label: getOnboardingLabel(geofence.label, lang), icon }
					:	null
			})
			.filter(notNull),
		'order',
	)
	const schedules = sortBy(filterOnboardingByAge(onboarding.schedules, userAge), 'order')
		.map((schedule): LabeledIconWithId | null => {
			const icon = SCHEDULE_ICON_MAP.get(schedule.content_key) // ← use content_key
			return icon ?
					{
						id: schedule.content_key,
						label: getOnboardingLabel(schedule.rule_name, lang),
						icon,
					}
				:	null
		})
		.filter(notNull)

	const vew = sortBy(
		filterOnboardingByAge(onboarding.view, userAge)
			.map((view): SectionBoxSmallWithId | null => {
				const statusOn = VEW_STATUS_TEXT_MAP.get(view.content_key) ?? 'On'

				const status = view.selected ? statusOn : 'Off'

				return {
					id: view.content_key,
					title: getOnboardingLabel(view.label, lang),
					subtitle: view.sublabel ? getOnboardingLabel(view.sublabel, lang) : undefined,
					status,
				}
			})
			.filter(notNull),
		'order',
	)

	const websiteRules: ColRuleItemWithId[] = [
		{
			id: 'adult_content',
			title: 'Adult Content',
			subtitle: APP_RULE_STATUS_TEXT_MAP.N,
			icon: APP_RULE_STATUS_ICON_MAP.N,
			colorClassName: 'text-red',
		},
	]

	return {
		allowance: getOnboardingAllowance(allowanceOpts, onboarding.allowance, userAge),
		appRules,
		locationAlerts,
		schedules,
		vew,
		websiteRules,
	}
}
